"use strict";

theme.Variants = (function () {
  function Variants(options) {
    this.container = options.container;
    this.variants = options.variants;
    this.singleOptionSelector = options.singleOptionSelector;
    this.originalSelectorId = options.originalSelectorId;
    this.enableHistoryState = options.enableHistoryState;
    this.currentlySelectedValues = this._getCurrentOptions();
    this.currentVariant = this._getVariantFromOptions();

    this.container
      .querySelectorAll(this.singleOptionSelector)
      .forEach((el) => {
        el.addEventListener("change", this._onSelectChange.bind(this));
      });
  }

  Variants.prototype = Object.assign({}, Variants.prototype, {
    _getCurrentOptions: function () {
      var result = [];

      this.container
        .querySelectorAll(this.singleOptionSelector)
        .forEach((el) => {
          var type = el.getAttribute("type");

          if (type === "radio" || type === "checkbox") {
            if (el.checked) {
              result.push({
                value: el.value,
                index: el.dataset.index,
              });
            }
          } else {
            result.push({
              value: el.value,
              index: el.dataset.index,
            });
          }
        });

      // remove any unchecked input values if using radio buttons or checkboxes
      result = theme.utils.compact(result);

      return result;
    },

    // Pull the number out of the option index name, e.g. 'option1' -> 1
    _numberFromOptionKey: function (key) {
      return parseInt(key.substr(-1));
    },

    // Options should be ordered from highest to lowest priority. Make sure that priority
    // is represented using weighted values when finding best match
    _getWeightedOptionMatchCount: function (variant) {
      return this._getCurrentOptions().reduce((count, { value, index }) => {
        const optionIndex = this._numberFromOptionKey(index);
        const weightedCount = 4 - optionIndex; // The lower the index, the better the match we have
        return variant[index] === value ? count + weightedCount : count;
      }, 0);
    },

    _getFullMatch(needsToBeAvailable) {
      const currentlySelectedOptions = this._getCurrentOptions();
      const variants = this.variants;

      return variants.find((variant) => {
        const isMatch = currentlySelectedOptions.every(({ value, index }) => {
          return variant[index] === value;
        });

        if (needsToBeAvailable) {
          return isMatch && variant.available;
        } else {
          return isMatch;
        }
      });
    },

    // Find a variant that is available and best matches last selected option
    _getClosestAvailableMatch: function (lastSelectedOption) {
      if (!lastSelectedOption) return null;

      const currentlySelectedOptions = this._getCurrentOptions();
      const variants = this.variants;

      const potentialAvailableMatches =
        lastSelectedOption &&
        variants.filter((variant) => {
          return (
            currentlySelectedOptions
              .filter(
                // Only match based selected options that are equal and preceeding the last selected option
                ({ value, index }) =>
                  this._numberFromOptionKey(index) <=
                  this._numberFromOptionKey(lastSelectedOption.index),
              )
              .every(({ value, index }) => {
                // Variant needs to have options that match the current and preceeding selection options
                return variant[index] === value;
              }) && variant.available
          );
        });

      return potentialAvailableMatches.reduce((bestMatch, variant) => {
        // If this is the first potential match we've found, store it as the best match
        if (bestMatch === null) return variant;

        // If this is not the first potential match, compare the number of options our current best match has in common
        // compared to the next contender.
        const bestMatchCount = this._getWeightedOptionMatchCount(
          bestMatch,
          lastSelectedOption,
        );
        const newCount = this._getWeightedOptionMatchCount(
          variant,
          lastSelectedOption,
        );

        return newCount > bestMatchCount ? variant : bestMatch;
      }, null);
    },

    _getVariantFromOptions: function (lastSelectedOption) {
      const availableFullMatch = this._getFullMatch(true);
      const closestAvailableMatch =
        this._getClosestAvailableMatch(lastSelectedOption);
      const fullMatch = this._getFullMatch(false);

      return availableFullMatch || closestAvailableMatch || fullMatch || null;
    },

    _updateInputState: function (variant, el) {
      return (input) => {
        if (variant === null) return;

        const index = input.dataset.index;
        const value = input.value;
        const type = input.getAttribute("type");

        if (type === "radio" || type === "checkbox") {
          input.checked = variant[index] === value;
        }
      };
    },

    _onSelectChange: function ({ srcElement }) {
      const optionSelectElements = this.container.querySelectorAll(
        this.singleOptionSelector,
      );

      // Get the best variant based on the current selection + last selected element
      const variant = this._getVariantFromOptions({
        index: srcElement.dataset.index,
        value: srcElement.value,
      });

      // Update DOM option input states based on the variant that was found
      optionSelectElements.forEach(
        this._updateInputState(variant, srcElement),
      );

      // Make sure our currently selected values are up to date after updating state of DOM
      const currentlySelectedValues = (this.currentlySelectedValues =
        this._getCurrentOptions());

      const detail = {
        variant,
        currentlySelectedValues,
        value: srcElement.value,
        index: srcElement.parentElement.dataset.index,
      };

      this.container.dispatchEvent(
        new CustomEvent("variantChange", { detail }),
      );
      document.dispatchEvent(new CustomEvent("variant:change", { detail }));

      if (!variant) {
        return;
      }

      this._updateMasterSelect(variant);
      this._updateImages(variant);
      this._updatePrice(variant);
      this._updateUnitPrice(variant);
      this._updateSKU(variant);
      this.currentVariant = variant;

      if (this.enableHistoryState) {
        this._updateHistoryState(variant);
      }
    },

    _updateImages: function (variant) {
      var variantImage = variant.featured_image || {};
      var currentVariantImage =
        (this.currentVariant && this.currentVariant.featured_image) || {};

      if (
        !variant.featured_image ||
        variantImage.src === currentVariantImage.src
      ) {
        return;
      }

      this.container.dispatchEvent(
        new CustomEvent("variantImageChange", {
          detail: {
            variant: variant,
          },
        }),
      );
    },

    _updatePrice: function (variant) {
      if (
        this.currentVariant &&
        variant.price === this.currentVariant.price &&
        variant.compare_at_price === this.currentVariant.compare_at_price
      ) {
        return;
      }

      this.container.dispatchEvent(
        new CustomEvent("variantPriceChange", {
          detail: {
            variant: variant,
          },
        }),
      );
    },

    _updateUnitPrice: function (variant) {
      if (
        this.currentVariant &&
        variant.unit_price === this.currentVariant.unit_price
      ) {
        return;
      }

      this.container.dispatchEvent(
        new CustomEvent("variantUnitPriceChange", {
          detail: {
            variant: variant,
          },
        }),
      );
    },

    _updateSKU: function (variant) {
      if (this.currentVariant && variant.sku === this.currentVariant.sku) {
        return;
      }

      this.container.dispatchEvent(
        new CustomEvent("variantSKUChange", {
          detail: {
            variant: variant,
          },
        }),
      );
    },

    _updateHistoryState: function (variant) {
      if (!history.replaceState || !variant) {
        return;
      }

      var newurl =
        window.location.protocol +
        "//" +
        window.location.host +
        window.location.pathname +
        "?variant=" +
        variant.id;
      window.history.replaceState({ path: newurl }, "", newurl);
    },

    _updateMasterSelect: function (variant) {
      this.container.querySelector(this.originalSelectorId).value =
        variant.id;
      // Force a change event so Shop Pay installments works after a variant is changed
      this.container
        .querySelector(this.originalSelectorId)
        .dispatchEvent(new Event("change", { bubbles: true }));
    },
  });

  return Variants;
})();

theme.PriceRange = (function () {
  var defaultStep = 10;
  var selectors = {
    priceRange: ".price-range",
    priceRangeSlider: ".price-range__slider",
    priceRangeInputMin: ".price-range__input-min",
    priceRangeInputMax: ".price-range__input-max",
    priceRangeDisplayMin: ".price-range__display-min",
    priceRangeDisplayMax: ".price-range__display-max",
  };

  function PriceRange(
    container,
    { onChange, onUpdate, ...sliderOptions } = {},
  ) {
    this.container = container;
    this.onChange = onChange;
    this.onUpdate = onUpdate;
    this.sliderOptions = sliderOptions || {};

    return this.init();
  }

  PriceRange.prototype = Object.assign({}, PriceRange.prototype, {
    init: function () {
      if (!this.container.classList.contains("price-range")) {
        throw new Error(
          "You must instantiate PriceRange with a valid container",
        );
      }

      this.formEl = this.container.closest("form");
      this.sliderEl = this.container.querySelector(
        selectors.priceRangeSlider,
      );
      this.inputMinEl = this.container.querySelector(
        selectors.priceRangeInputMin,
      );
      this.inputMaxEl = this.container.querySelector(
        selectors.priceRangeInputMax,
      );
      this.displayMinEl = this.container.querySelector(
        selectors.priceRangeDisplayMin,
      );
      this.displayMaxEl = this.container.querySelector(
        selectors.priceRangeDisplayMax,
      );

      this.minRange = parseFloat(this.container.dataset.min) || 0;
      this.minValue = parseFloat(this.container.dataset.minValue) || 0;
      this.maxRange = parseFloat(this.container.dataset.max) || 100;
      this.maxValue =
        parseFloat(this.container.dataset.maxValue) || this.maxRange;

      return this.createPriceRange();
    },

    createPriceRange: function () {
      if (
        this.sliderEl &&
        this.sliderEl.noUiSlider &&
        typeof this.sliderEl.noUiSlider.destroy === "function"
      ) {
        this.sliderEl.noUiSlider.destroy();
      }

      var slider = noUiSlider.create(this.sliderEl, {
        connect: true,
        step: defaultStep,
        ...this.sliderOptions,
        // Do not allow overriding these options
        start: [this.minValue, this.maxValue],
        range: {
          min: this.minRange,
          max: this.maxRange,
        },
      });

      slider.on("update", (values) => {
        this.displayMinEl.innerHTML = theme.Currency.formatMoney(
          values[0],
          theme.settings.moneyFormat,
        );
        this.displayMaxEl.innerHTML = theme.Currency.formatMoney(
          values[1],
          theme.settings.moneyFormat,
        );

        if (this.onUpdate) {
          this.onUpdate(values);
        }
      });

      slider.on("change", (values) => {
        this.inputMinEl.value = values[0];
        this.inputMaxEl.value = values[1];

        if (this.onChange) {
          const formData = new FormData(this.formEl);
          this.onChange(formData);
        }
      });

      return slider;
    },
  });

  return PriceRange;
})();

theme.AjaxProduct = (function () {
  var status = {
    loading: false,
  };

  function ProductForm(form, submit, args) {
    this.form = form;
    this.args = args;

    var submitSelector = submit ? submit : ".add-to-cart";

    if (this.form) {
      this.addToCart = form.querySelector(submitSelector);
      this.form.addEventListener("submit", this.addItemFromForm.bind(this));
    }
  }

  ProductForm.prototype = Object.assign({}, ProductForm.prototype, {
    addItemFromForm: function (evt, callback) {
      evt.preventDefault();

      if (status.loading) {
        return;
      }

      // Loading indicator on add to cart button
      this.addToCart.classList.add("btn--loading");

      status.loading = true;

      var data = theme.utils.serialize(this.form);

      fetch(theme.routes.cartAdd, {
        method: "POST",
        body: data,
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-Requested-With": "XMLHttpRequest",
        },
      })
        .then((response) => response.json())
        .then(
          function (data) {
            if (data.status === 422) {
              this.error(data);
            } else {
              var product = data;
              this.success(product);
            }

            status.loading = false;
            this.addToCart.classList.remove("btn--loading");

            // Reload page if adding product from a section on the cart page
            if (document.body.classList.contains("template-cart")) {
              window.scrollTo(0, 0);
              location.reload();
            }
          }.bind(this),
        );
    },

    success: function (product) {
      var errors = this.form.querySelector(".errors");
      if (errors) {
        errors.remove();
      }

      document.dispatchEvent(
        new CustomEvent("ajaxProduct:added", {
          detail: {
            product: product,
            addToCartBtn: this.addToCart,
          },
        }),
      );

      if (this.args && this.args.scopedEventId) {
        document.dispatchEvent(
          new CustomEvent("ajaxProduct:added:" + this.args.scopedEventId, {
            detail: {
              product: product,
              addToCartBtn: this.addToCart,
            },
          }),
        );
      }
    },

    error: function (error) {
      if (!error.description) {
        console.warn(error);
        return;
      }

      var errors = this.form.querySelector(".errors");
      if (errors) {
        errors.remove();
      }

      var errorDiv = document.createElement("div");
      errorDiv.classList.add("errors", "text-center");
      errorDiv.textContent = error.description;
      this.form.append(errorDiv);

      document.dispatchEvent(
        new CustomEvent("ajaxProduct:error", {
          detail: {
            errorMessage: error.description,
          },
        }),
      );

      if (this.args && this.args.scopedEventId) {
        document.dispatchEvent(
          new CustomEvent("ajaxProduct:error:" + this.args.scopedEventId, {
            detail: {
              errorMessage: error.description,
            },
          }),
        );
      }
    },
  });

  return ProductForm;
})();

theme.QtySelector = (function () {
  var selectors = {
    input: ".js-qty__num",
    plus: ".js-qty__adjust--plus",
    minus: ".js-qty__adjust--minus",
  };

  function QtySelector(el, options) {
    this.wrapper = el;
    this.plus = el.querySelector(selectors.plus);
    this.minus = el.querySelector(selectors.minus);
    this.input = el.querySelector(selectors.input);
    this.minValue = this.input.getAttribute("min") || 1;

    var defaults = {
      namespace: null,
      isCart: false,
      key: this.input.dataset.id,
    };

    this.options = Object.assign({}, defaults, options);

    this.init();
  }

  QtySelector.prototype = Object.assign({}, QtySelector.prototype, {
    init: function () {
      this.plus.addEventListener(
        "click",
        function () {
          var qty = this._getQty();
          this._change(qty + 1);
        }.bind(this),
      );

      this.minus.addEventListener(
        "click",
        function () {
          var qty = this._getQty();
          this._change(qty - 1);
        }.bind(this),
      );

      this.input.addEventListener(
        "change",
        function (evt) {
          this._change(this._getQty());
        }.bind(this),
      );
    },

    _getQty: function () {
      var qty = this.input.value;
      if (parseFloat(qty) == parseInt(qty) && !isNaN(qty)) {
        // We have a valid number!
      } else {
        // Not a number. Default to 1.
        qty = 1;
      }
      return parseInt(qty);
    },

    _change: function (qty) {
      if (qty <= this.minValue) {
        qty = this.minValue;
      }

      this.input.value = qty;

      if (this.options.isCart) {
        document.dispatchEvent(
          new CustomEvent("cart:quantity" + this.options.namespace, {
            detail: [this.options.key, qty, this.wrapper],
          }),
        );
      }
    },
  });

  return QtySelector;
})();

theme.VariantAvailability = (function () {
  var classes = {
    disabled: "disabled",
  };

  function availability(args) {
    this.type = args.type;
    this.variantsObject = args.variantsObject;
    this.currentVariantObject = args.currentVariantObject;
    this.container = args.container;
    this.namespace = args.namespace;

    this.init();
  }

  availability.prototype = Object.assign({}, availability.prototype, {
    init: function () {
      this.container.on(
        "variantChange" + this.namespace,
        this.setAvailability.bind(this),
      );

      // Set default state based on current selected variant
      this.setInitialAvailability();
    },

    // Create a list of all options. If any variant exists and is in stock with that option, it's considered available
    createAvailableOptionsTree(variants, currentlySelectedValues) {
      // Reduce variant array into option availability tree
      return variants.reduce(
        (options, variant) => {
          // Check each option group (e.g. option1, option2, option3) of the variant
          Object.keys(options).forEach((index) => {
            if (variant[index] === null) return;

            let entry = options[index].find(
              (option) => option.value === variant[index],
            );

            if (typeof entry === "undefined") {
              // If option has yet to be added to the options tree, add it
              entry = { value: variant[index], soldOut: true };
              options[index].push(entry);
            }

            const currentOption1 = currentlySelectedValues.find(
              ({ value, index }) => index === "option1",
            );
            const currentOption2 = currentlySelectedValues.find(
              ({ value, index }) => index === "option2",
            );

            switch (index) {
              case "option1":
                // Option1 inputs should always remain enabled based on all available variants
                entry.soldOut =
                  entry.soldOut && variant.available ? false : entry.soldOut;
                break;
              case "option2":
                // Option2 inputs should remain enabled based on available variants that match first option group
                if (
                  currentOption1 &&
                  variant["option1"] === currentOption1.value
                ) {
                  entry.soldOut =
                    entry.soldOut && variant.available
                      ? false
                      : entry.soldOut;
                }
              case "option3":
                // Option 3 inputs should remain enabled based on available variants that match first and second option group
                if (
                  currentOption1 &&
                  variant["option1"] === currentOption1.value &&
                  currentOption2 &&
                  variant["option2"] === currentOption2.value
                ) {
                  entry.soldOut =
                    entry.soldOut && variant.available
                      ? false
                      : entry.soldOut;
                }
            }
          });

          return options;
        },
        { option1: [], option2: [], option3: [] },
      );
    },

    setInitialAvailability: function () {
      this.container
        .querySelectorAll(".variant-input-wrap")
        .forEach((group) => {
          this.disableVariantGroup(group);
        });

      const currentlySelectedValues = this.currentVariantObject.options.map(
        (value, index) => {
          return { value, index: `option${index + 1}` };
        },
      );
      const initialOptions = this.createAvailableOptionsTree(
        this.variantsObject,
        currentlySelectedValues,
        this.currentVariantObject,
      );

      for (var [option, values] of Object.entries(initialOptions)) {
        this.manageOptionState(option, values);
      }
    },

    setAvailability: function (evt) {
      const {
        value: lastSelectedValue,
        index: lastSelectedIndex,
        currentlySelectedValues,
        variant,
      } = evt.detail;

      // Object to hold all options by value.
      // This will be what sets a button/dropdown as
      // sold out or unavailable (not a combo set as purchasable)
      const valuesToManage = this.createAvailableOptionsTree(
        this.variantsObject,
        currentlySelectedValues,
        variant,
        lastSelectedIndex,
        lastSelectedValue,
      );

      // Loop through all option levels and send each
      // value w/ args to function that determines to show/hide/enable/disable
      for (var [option, values] of Object.entries(valuesToManage)) {
        this.manageOptionState(option, values, lastSelectedValue);
      }
    },

    manageOptionState: function (option, values) {
      var group = this.container.querySelector(
        '.variant-input-wrap[data-index="' + option + '"]',
      );

      // Loop through each option value
      values.forEach((obj) => {
        this.enableVariantOption(group, obj);
      });
    },

    enableVariantOption: function (group, obj) {
      // Selecting by value so escape it
      var value = obj.value.replace(
        /([ #;&,.+*~\':"!^$[\]()=>|\/@])/g,
        "\\$1",
      );

      if (this.type === "dropdown") {
        group.querySelector('option[value="' + value + '"]').disabled = false;
      } else {
        var buttonGroup = group.querySelector(
          '.variant-input[data-value="' + value + '"]',
        );
        var input = buttonGroup.querySelector("input");
        var label = buttonGroup.querySelector("label");

        // Variant exists - enable & show variant
        input.classList.remove(classes.disabled);
        label.classList.remove(classes.disabled);

        // Variant sold out - cross out option (remains selectable)
        if (obj.soldOut) {
          input.classList.add(classes.disabled);
          label.classList.add(classes.disabled);
        }
      }
    },

    disableVariantGroup: function (group) {
      if (this.type === "dropdown") {
        group.querySelectorAll("option").forEach((option) => {
          option.disabled = true;
        });
      } else {
        group.querySelectorAll("input").forEach((input) => {
          input.classList.add(classes.disabled);
        });
        group.querySelectorAll("label").forEach((label) => {
          label.classList.add(classes.disabled);
        });
      }
    },
  });

  return availability;
})();

// Video modal will auto-initialize for any anchor link that points to YouTube
// MP4 videos must manually be enabled with:
//   - .product-video-trigger--mp4 (trigger button)
//   - .product-video-mp4-sound video player element (cloned into modal)
//     - see media.liquid for example of this
